import { useCallback, useEffect, useRef, useState } from "react";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { getHealthConnections } from "../../Services/connectedHealth";

/**
 * Loads `GET /api/v4/health/connections`. `reload()` refetches without
 * dropping the rendered data (so a toggle does not flash the page to a
 * spinner); the first load and a retry after an error show the loading state.
 */
export const useConnectedHealth = () => {
  const [state, setState] = useState({ status: "loading", data: null });
  const alive = useRef(true);

  const load = useCallback(async () => {
    setState((prev) => (prev.data ? prev : { status: "loading", data: null }));
    try {
      const data = await getHealthConnections();
      if (alive.current) setState({ status: "ready", data });
      return data;
    } catch (error) {
      if (alive.current) {
        setState((prev) => (prev.data ? prev : { status: "error", data: null }));
      }
      return null;
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  return { ...state, reload: load };
};

/**
 * The global header floats a transparent bar over the page that swallows
 * clicks on a back button, so these screens hide it and restore it on unmount
 * (same as the Explore sub-pages).
 */
export const useStandaloneHeader = () => {
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  useEffect(() => {
    setHeaderContent?.(false);
    setHeaderContentRightSideContent?.(false);
    return () => {
      setHeaderContent?.(true);
      setHeaderContentRightSideContent?.(true);
    };
  }, [setHeaderContent, setHeaderContentRightSideContent]);
};
